import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Eye, EyeOff, RefreshCw, Mail, Phone, Calendar as CalendarIcon,
  User, FileText, Home as HomeIcon, ArrowUpRight, MessageSquare, LogOut, Lock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || 'Tsalach2025';

function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('quotes');
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/quote-requests`),
        axios.get(`${API}/contact-messages`),
      ]);
      setQuoteRequests([...q.data].reverse());
      setContactMessages([...c.data].reverse());
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword('');
  };

  const formatDate = (ts) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0D1B2A] text-white flex items-center justify-center p-6 relative overflow-hidden" data-testid="admin-login-screen">
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] rounded-full bg-[#C9A84C]/5 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full bg-[#0A2540]/40 blur-[100px] pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-full max-w-md"
        >
          <a href="/" className="inline-flex items-center gap-3 mb-12 group" data-testid="admin-back-home">
            <div className="relative inline-block">
              <div
                className="absolute inset-0 -m-5 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(245,240,230,0.28) 0%, rgba(245,240,230,0) 70%)',
                  filter: 'blur(10px)',
                }}
                aria-hidden="true"
              />
              <img
                src="/assets/tsalach-logo.png"
                alt="Tsalach Cleaning Services"
                className="relative h-16 w-auto object-contain"
                style={{
                  filter:
                    'drop-shadow(0 2px 6px rgba(212,175,55,0.3)) drop-shadow(0 0 14px rgba(245,240,230,0.4))',
                }}
              />
            </div>
          </a>

          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase">Restricted</span>
          </div>
          <h1 className="font-serif leading-[1] tracking-tight mb-3" style={{ fontSize: 'clamp(2.5rem, 6vw, 3.75rem)' }}>
            Studio
            <br />
            <span className="italic font-light text-[#C9A84C]">access.</span>
          </h1>
          <p className="text-white/50 mb-12 max-w-sm">Sign in to view live quote requests and client messages.</p>

          <form onSubmit={handleLogin} className="space-y-8">
            <label className="block">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A84C] mb-3 flex items-center gap-2">
                <Lock className="w-3 h-3" /> Password
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#C9A84C] py-4 pr-10 text-lg font-serif text-white outline-none transition-colors placeholder:text-white/20"
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#C9A84C] transition-colors"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {error && (
                <p className="text-red-400 text-sm mt-3" data-testid="admin-login-error">{error}</p>
              )}
            </label>

            <button
              type="submit"
              className="group relative overflow-hidden w-full bg-[#C9A84C] text-[#0D1B2A] py-4 text-xs tracking-[0.3em] uppercase font-semibold flex items-center justify-center gap-3"
              data-testid="admin-login-button"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-[#D9BC6E] to-[#C9A84C] -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
              <span className="relative">Enter Dashboard</span>
              <ArrowUpRight className="relative w-4 h-4 group-hover:rotate-45 transition-transform" />
            </button>
          </form>

          <a
            href="/"
            className="mt-12 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-white/40 hover:text-[#C9A84C] transition-colors"
          >
            <HomeIcon className="w-3.5 h-3.5" />
            Return to website
          </a>
        </motion.div>
      </div>
    );
  }

  // Authenticated view
  const items = activeTab === 'quotes' ? quoteRequests : contactMessages;

  return (
    <div className="min-h-screen bg-[#0D1B2A] text-white" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#0D1B2A]/95 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative inline-block">
              <div
                className="absolute inset-0 -m-3 pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(245,240,230,0.22) 0%, rgba(245,240,230,0) 70%)',
                  filter: 'blur(8px)',
                }}
                aria-hidden="true"
              />
              <img
                src="/assets/tsalach-logo.png"
                alt="Tsalach Cleaning Services"
                className="relative h-11 w-auto object-contain"
                style={{
                  filter: 'drop-shadow(0 0 10px rgba(245,240,230,0.4))',
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={fetchData}
              disabled={loading}
              className="group flex items-center gap-2 border border-white/15 hover:border-[#C9A84C] hover:text-[#C9A84C] px-4 md:px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-500`} />
              <span className="hidden md:inline">Refresh</span>
            </button>
            <a
              href="/"
              className="hidden md:flex items-center gap-2 border border-white/15 hover:border-[#C9A84C] hover:text-[#C9A84C] px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase transition-colors"
              data-testid="admin-website-link"
            >
              <HomeIcon className="w-3.5 h-3.5" /> Website
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-[#C9A84C] text-[#0D1B2A] px-4 md:px-5 py-2.5 text-[10px] tracking-[0.25em] uppercase font-semibold"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 md:py-20">
        {/* Heading */}
        <div className="mb-12 md:mb-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-xs tracking-[0.4em] uppercase">Inbox</span>
          </div>
          <h1 className="font-serif leading-[1] tracking-tight" style={{ fontSize: 'clamp(2.75rem, 7vw, 5.5rem)' }}>
            Live signals
            <br />
            <span className="italic font-light text-[#C9A84C]">from the field.</span>
          </h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 md:gap-8 mb-16">
          {[
            { id: 'quotes', label: 'Quote requests', count: quoteRequests.length, icon: FileText },
            { id: 'contacts', label: 'Contact messages', count: contactMessages.length, icon: MessageSquare },
          ].map((s) => {
            const Icon = s.icon;
            const isActive = activeTab === s.id;
            return (
              <button
                key={s.id}
                onClick={() => setActiveTab(s.id)}
                className={`group text-left border-l p-6 md:p-8 transition-all ${
                  isActive
                    ? 'border-[#C9A84C] bg-white/[0.02]'
                    : 'border-white/10 hover:border-white/30'
                }`}
                data-testid={`${s.id}-tab`}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`w-5 h-5 transition-colors ${isActive ? 'text-[#C9A84C]' : 'text-white/40'}`} />
                  <ArrowUpRight className={`w-4 h-4 transition-all ${
                    isActive
                      ? 'text-[#C9A84C] rotate-0 opacity-100'
                      : 'text-white/30 -rotate-45 opacity-0 group-hover:opacity-100'
                  }`} />
                </div>
                <div
                  className={`font-serif tabular-nums leading-none mb-3 transition-colors ${isActive ? 'text-[#C9A84C]' : 'text-white'}`}
                  style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
                >
                  {String(s.count).padStart(2, '0')}
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">{s.label}</div>
              </button>
            );
          })}
        </div>

        {/* Items */}
        <div className="border-t border-white/10">
          <div className="flex items-center justify-between py-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#C9A84C]">
              {activeTab === 'quotes' ? 'Quote requests' : 'Contact messages'}
            </div>
            <div className="text-xs text-white/40 tabular-nums">
              {String(items.length).padStart(2, '0')} total
            </div>
          </div>

          {loading ? (
            <div className="text-center py-32">
              <RefreshCw className="w-6 h-6 text-[#C9A84C] animate-spin mx-auto mb-4" />
              <p className="text-white/40 text-xs tracking-[0.3em] uppercase">Loading</p>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-32 border-t border-white/5">
              <p className="font-serif text-2xl text-white/30 italic">No {activeTab === 'quotes' ? 'requests' : 'messages'} yet.</p>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                className="divide-y divide-white/5 border-t border-white/5"
              >
                {items.map((item, idx) => (
                  <motion.article
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: idx * 0.04 }}
                    className="group py-8 md:py-10 grid md:grid-cols-12 gap-6 hover:bg-white/[0.02] transition-colors px-2 md:px-6"
                    data-testid={activeTab === 'quotes' ? 'quote-item' : 'contact-item'}
                  >
                    {/* Index + name */}
                    <div className="md:col-span-3 flex items-start gap-4">
                      <span className="text-[10px] text-[#C9A84C] tabular-nums tracking-wider mt-1.5">
                        {String(items.length - idx).padStart(2, '0')}
                      </span>
                      <div>
                        <div className="font-serif text-xl md:text-2xl text-white mb-1">{item.name}</div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 flex items-center gap-1.5">
                          <CalendarIcon className="w-3 h-3" /> {formatDate(item.timestamp)}
                        </div>
                      </div>
                    </div>

                    {/* Contact */}
                    <div className="md:col-span-3 space-y-2 text-sm">
                      <a href={`mailto:${item.email}`} className="flex items-center gap-2 text-white/70 hover:text-[#C9A84C] transition-colors break-all">
                        <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{item.email}</span>
                      </a>
                      <a href={`tel:${item.phone}`} className="flex items-center gap-2 text-white/70 hover:text-[#C9A84C] transition-colors">
                        <Phone className="w-3.5 h-3.5 flex-shrink-0" />
                        <span>{item.phone}</span>
                      </a>
                    </div>

                    {/* Service / property (quotes only) */}
                    {activeTab === 'quotes' ? (
                      <div className="md:col-span-2 space-y-2">
                        <div>
                          <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">Service</div>
                          <div className="text-sm text-white">{item.service_type}</div>
                        </div>
                        <div>
                          <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">Property</div>
                          <div className="text-sm text-white">{item.property_type}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="md:col-span-2" />
                    )}

                    {/* Message */}
                    <div className="md:col-span-4">
                      {item.message ? (
                        <>
                          <div className="text-[9px] tracking-[0.3em] uppercase text-[#C9A84C]/70 mb-2 flex items-center gap-1.5">
                            <User className="w-3 h-3" /> Notes
                          </div>
                          <p className="text-sm text-white/70 leading-relaxed font-light italic">"{item.message}"</p>
                        </>
                      ) : (
                        <p className="text-sm text-white/30 italic">No additional notes.</p>
                      )}
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>

      <footer className="border-t border-white/5 bg-[#07111C]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs text-white/30">© {new Date().getFullYear()} Tsalach Studio · Admin</p>
          <p className="text-[10px] tracking-[0.4em] uppercase text-[#C9A84C]/50">Clean Spaces · Healthy Places · Happy Faces</p>
        </div>
      </footer>
    </div>
  );
}

export default AdminDashboard;
